/*
 *  -----------------------------------------------------------------------
 *    <copyright file="assessments-svt-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import cn from 'clsx';
import {
    ASSESSMENT_SVT_COLUMNS,
    ASSESSMENTS_MODAL_CLOSE_ACTIONS,
} from '@components/admin/assessments-svt-grid/assessments-svt-grid.data';
import {
    AssessmentsSVTGridProps,
    StudyAssessmentFieldsExtended,
} from '@components/admin/assessments-svt-grid/assessments-svt-grid.interface';
import AssignSVTAssessmentDialog from '@components/admin/assign-svt-assessment';
import UnassignAssessmentConfirmanionDialog from '@components/admin/unassign-assessment-confirmation-dialog';
import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import {
    ExcelExportConfig,
    IActionItemV2,
} from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import { useBoolean, useLocalState, useTranslation } from 'hooks';
import NotificationService from 'utils/notification';

import Loading from 'views/components/core/loading';
import './styles.scss';
import { convertRules } from '@utils/convertor';
import { addCRFNameToAssessment } from '@utils/filter';
import { StudyAssessmentFields } from '@queries/generated/fragments.generated';
import { GET_STUDY_CRF_ASSESSMENTS, GET_STUDY_CRFS } from '@queries/study-crfs';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';

const AssessmentsSVTGrid = ({
    currentScheduleId,
    currentScheduleVersion,
    canEdit,
    currentVisit,
    currentItem,
    setCurrentItem,
    switchMenu,
    className,
    assessmentAssociation,
}: AssessmentsSVTGridProps) => {
    const { t } = useTranslation();
    const { state: localState } = useLocalState();
    const study = localState.selectedStudy;
    const studyId = study.id;

    const [currentAssessment, setCurrentAssessment] = useState<StudyAssessmentFields | null>(currentItem ?? null);

    const openAssignAssessment = useBoolean(false);
    const openUnassignDialog = useBoolean(false);

    const { data: crfsData, loading } = useQuery(GET_STUDY_CRFS, { variables: { studyId } });
    const { data: assessmentsData, loading: loadingAssessments } = useQuery(GET_STUDY_CRF_ASSESSMENTS, {
        variables: { studyId: studyId ?? null },
    });

    const associatedView = !!currentVisit;

    const extraMenuItems: ExtraMenuItem[] = associatedView
        ? [
              {
                  position: ExtraMenuItemPosition.expandMenu,
                  customClassName: 'grid-header__item--always-visible',
                  expandMenuItems: [
                      {
                          label: 'admin:assignAssessments',
                          onClick: () => openAssignAssessment.setTrue(),
                          icon: ['fal', 'add'],
                          enabled: canEdit && !assessmentAssociation,
                      },
                  ],
              },
          ]
        : [];

    const getRowActions = (assessment: StudyAssessmentFields): IActionItemV2[] => [
        {
            label: 'admin:unassignAssessment',
            icon: ['fal', 'unlink'],
            onClick: () => {
                setCurrentAssessment(assessment);
                openUnassignDialog.setTrue();
            },
            enabled: canEdit && !assessmentAssociation,
        },
    ];

    const closeAssignDialog = (action: string | boolean | undefined | any) => {
        if (!action) {
            openAssignAssessment.setFalse();
        } else if (action === ASSESSMENTS_MODAL_CLOSE_ACTIONS.ERROR) {
            NotificationService.showNotification(t('errorMessage'));
        } else {
            if (
                action?.action === ASSESSMENTS_MODAL_CLOSE_ACTIONS.SUCCESS ||
                action?.action === ASSESSMENTS_MODAL_CLOSE_ACTIONS.SUCCESS_AND_CLOSE
            ) {
                NotificationService.showNotification(
                    t('admin:successfulAssignedAssessmentsToVisit', {
                        amount: action?.amount,
                        id: currentVisit?.recordId || currentVisit?.id,
                    }),
                );
            }

            if (action?.action === ASSESSMENTS_MODAL_CLOSE_ACTIONS.SUCCESS_AND_CLOSE) {
                openAssignAssessment.setFalse();
            }
        }
    };

    const closeUnassignDialog = (action: string | boolean | undefined) => {
        if (action === ASSESSMENTS_MODAL_CLOSE_ACTIONS.ERROR) {
            NotificationService.showNotification(t('errorMessage'));
        } else {
            if (action === ASSESSMENTS_MODAL_CLOSE_ACTIONS.UNASSIGN) {
                NotificationService.showNotification(
                    t('admin:assessmentUnassignedMessage', {
                        assessmentId: currentAssessment?.recordId || currentAssessment?.id,
                        visitId: currentVisit?.recordId || currentVisit?.id,
                    }),
                );
            }

            openUnassignDialog.setFalse();
        }
    };

    const onSelectItem = (assessment: StudyAssessmentFields) => {
        setCurrentAssessment(assessment);
        if (setCurrentItem) {
            setCurrentItem(assessment);
        }
    };

    useEffect(() => {
        if (assessmentsData && setCurrentItem) {
            const reorderedAssessments: StudyAssessmentFields[] = orderBy(assessmentsData.crfAssessments, [
                { field: 'active', dir: 'desc' },
                { field: 'name', dir: 'asc' },
            ]);
            onSelectItem(reorderedAssessments[0]);
        }
    }, []);

    const getAssessments = (
        allAssessments: StudyAssessmentFields[],
        crfToAssessAssociation: boolean | null | undefined,
    ) => {
        let assessmentsToShow: StudyAssessmentFieldsExtended[] = [];

        if (associatedView && currentVisit) {
            const visitAssessments = currentVisit.assessments;
            if (crfToAssessAssociation) {
                if (currentVisit.crfs?.length) {
                    currentVisit.crfs.forEach((crf) => {
                        const assignedAssessment = allAssessments.filter((assessment) => assessment.crfId === crf?.id);
                        if (assignedAssessment?.length) {
                            assessmentsToShow.push(...assignedAssessment);
                        }
                    });
                }
            } else if (visitAssessments?.length) {
                // NOTE: backend should provide a decent type for assessments array
                assessmentsToShow = visitAssessments;
            }
        } else {
            assessmentsToShow = allAssessments;
        }

        assessmentsToShow = addCRFNameToAssessment(assessmentsToShow, crfsData?.crfs, assessmentAssociation);
        assessmentsToShow = assessmentsToShow.map((assessmentItem) => ({
            ...assessmentItem,
            _assessmentRules: convertRules(assessmentItem.assessmentRules),
        }));
        const newData = restructuredUpdatedCreatedBy(assessmentsToShow);
        assessmentsToShow =
            orderBy(newData, [
                { field: 'active', dir: 'desc' },
                { field: 'name', dir: 'asc' },
            ]) ?? [];

        return assessmentsToShow;
    };

    if (!studyId || loading || loadingAssessments) return <Loading />;

    const assessments = getAssessments(assessmentsData.crfAssessments, assessmentAssociation);

    const exportFileName = `${study?.studyNumber}_SVT_Assessments ${
        currentScheduleVersion ? `${currentScheduleVersion}` : ''
    }`;
    const exportConfig: ExcelExportConfig = {
        fileName: exportFileName,
        useColumn: true,
        useData: true,
    };

    return (
        <div className="tab-container">
            {openAssignAssessment.value && !!currentVisit && (
                <AssignSVTAssessmentDialog
                    currentVisit={currentVisit}
                    currentScheduleId={currentScheduleId}
                    studyId={studyId}
                    close={closeAssignDialog}
                />
            )}
            {openUnassignDialog.value && !!currentVisit && (
                <UnassignAssessmentConfirmanionDialog
                    studyId={studyId}
                    currentScheduleId={currentScheduleId}
                    currentAssessment={currentAssessment}
                    currentVisit={currentVisit}
                    close={closeUnassignDialog}
                />
            )}
            <ExpandableGridV2
                className={cn('grid-y-scroll', className)}
                title={t(associatedView ? 'subject:assessments' : 'subject:allStudyAssessments')}
                customKey="assessments-svt-grid"
                id="svtAssessmentsGrid"
                key="svtAssessmentsGrid"
                items={assessments}
                hideCheckbox
                hideExpand
                scrollable="scrollable"
                groupable
                reorderable
                resizable
                pageable={false}
                setCurrentItem={onSelectItem}
                showFirstColumn={associatedView}
                selectable={{ enabled: !associatedView }}
                fixedScroll={!associatedView}
                defaultSelected={!associatedView ? (currentAssessment ?? assessments[0]) : undefined}
                switchMenu={switchMenu}
                rowActions={associatedView ? getRowActions : undefined}
                extraMenuItems={extraMenuItems}
                columns={ASSESSMENT_SVT_COLUMNS(t)}
                exportConfig={!associatedView ? exportConfig : null}
            />
        </div>
    );
};

export default AssessmentsSVTGrid;
